import { useEffect, useState, Fragment, useRef } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { IoStatsChart, IoTrashOutline, IoArrowUpOutline, IoArrowDownOutline } from "react-icons/io5";
import { HiChevronRight, HiDotsVertical } from "react-icons/hi";
import Table from "@/components/Table";
import api from "@/services/api";
import { HiPencil, HiPlus } from "react-icons/hi2";
import TopMenu from "./components/TopMenu";
import { Menu, Transition } from "@headlessui/react";
import ItemModal from "../components/itemModal";

export default ({ project }) => {
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    search: "",
    nnls: [],
    statuses: ["TODO", "READY_TO_DEV", "IN_PROGRESS", "NEED_INFO"],
    module_ids: [],
    roadlog_tags: [],
    is_in_backlog: "",
  });
  const [budgets, setBudgets] = useState([]);
  const [sortBy, setSortBy] = useState({ key: "title", value: 1 });
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedModules, setExpandedModules] = useState(new Set([]));
  const [editingId, setEditingId] = useState(null);

  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const itemModalId = query.get("item_modal_id");

  useEffect(() => {
    fetchModules();
    fetchItems();
    fetchBudgets();
  }, [filters]);

  const fetchModules = async () => {
    setLoading(true);
    try {
      const { data, ok } = await api.post("/roadlog_module/search", {
        project_id: project._id,
      });

      if (!ok) return toast.error("Failed to fetch modules");

      // Create a default module for items without module
      const modules = [{ _id: "-", title: "-" }, ...data];
      setModules(modules);

      // Initialize expanded modules with all modules
      if (expandedModules.size === 0) {
        setExpandedModules(new Set(modules.map((m) => m._id)));
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch modules");
    }
  };

  const fetchItems = async () => {
    try {
      console.log(filters);
      const { data, ok } = await api.post("/roadlog_item/search", {
        project_id: project._id,
        exclude_categories: ["BUG"],
        ...filters,
      });

      if (!ok) return toast.error("Failed to fetch roadlog items");
      setItems(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch roadlog items");
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgets = async () => {
    try {
      const { data, ok } = await api.post("/budget/search", {
        projectId: project._id,
        status: "active",
      });

      if (!ok) return toast.error("Failed to fetch budgets");
      setBudgets(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch budgets");
    }
  };

  const handleDelete = async (itemId) => {
    try {
      if (!window.confirm("Are you sure you want to delete this item?")) return;
      const { ok } = await api.remove(`/roadlog_item/${itemId}`);
      if (!ok) return toast.error("Failed to delete item");

      toast.success("Item deleted successfully");
      setItems(items.filter((i) => i._id !== itemId));
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete item");
    }
  };

  const handleSave = async (itemId, value) => {
    try {
      const { ok, data } = await api.put(`/roadlog_item/${itemId}`, value);
      if (!ok) return toast.error("Failed to update");

      const newItems = items.map((item) => (item._id === itemId ? { ...item, ...value } : item));

      setItems(newItems);
      toast.success("Updated successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update");
    }
  };

  // Add toggle function for modules
  const toggleModule = (moduleId) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };

  const handleSelectModule = (moduleItems) => {
    const allSelected = moduleItems.every((item) => selectedItems.some((selectedItem) => selectedItem._id === item._id));

    if (allSelected) return setSelectedItems(selectedItems.filter((item) => !moduleItems.some((moduleItem) => moduleItem._id === item._id)));

    const newSelectedItems = [...selectedItems];
    moduleItems.forEach((item) => {
      if (!newSelectedItems.some((selectedItem) => selectedItem._id === item._id)) {
        newSelectedItems.push(item);
      }
    });
    setSelectedItems(newSelectedItems);
  };

  const moveItem = async (direction, moduleId, currentIndex) => {
    try {
      const moduleItems = items.filter((item) => (item.module_id || "-") === moduleId).sort((a, b) => a.index - b.index);

      if (!moduleItems.length) return;

      const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= moduleItems.length) return;

      const itemToMove = moduleItems[currentIndex];
      const itemToSwap = moduleItems[newIndex];

      await api.put(`/roadlog_item/${itemToMove._id}`, {
        index: itemToSwap.index,
      });

      await api.put(`/roadlog_item/${itemToSwap._id}`, {
        index: itemToMove.index,
      });

      await fetchModules();
      await fetchItems();
      toast.success("Item moved successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to move item");
    }
  };

  return (
    <div className="space-y-1">
      <TopMenu
        filters={filters}
        setFilters={setFilters}
        project={project}
        items={items}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        onMultipleDelete={() => {
          setItems(items.filter((i) => !selectedItems.some((s) => s._id === i._id)));
          setSelectedItems([]);
        }}
        onTicketCreated={(newTicket) => {
          setItems([...items, newTicket]);
        }}
      />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <Table
          total={items.length}
          header={[
            {
              title: (
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedItems(items);
                      setExpandedModules(new Set(modules.map((m) => m._id)));
                    } else {
                      setSelectedItems([]);
                    }
                  }}
                />
              ),
              position: "center",
            },
            { title: "Title", key: "title" },
            { title: "Owner" },
            { title: "Budget" },
            { title: "Days Required" },
            { title: "Estimation", key: "estimation" },
            { title: "Status", key: "status" },
            { title: "" },
          ]}
          loading={loading}
          sort={{ [sortBy.key]: sortBy.value }}
          onSort={(e) => setSortBy(sortBy.key === e ? { key: e, value: sortBy.value * -1 } : { key: e, value: 1 })}>
          {modules
            .sort((a, b) => {
              if (a._id === "-") return -1;
              if (b._id === "-") return 1;
              return a.title.localeCompare(b.title);
            })
            .map((module) => {
              const moduleItems = items
                .filter((item) => (item.module_id || "-") === module._id)
                .sort((a, b) => {
                  // Sort by index from smallest to largest
                  const indexA = a.index || 0;
                  const indexB = b.index || 0;
                  return indexA - indexB;
                });
              if (moduleItems.length === 0) return null;

              return (
                <Fragment key={module._id}>
                  <tr
                    onClick={() => toggleModule(module._id)}
                    className={`
                      cursor-pointer border-y border-gray-200
                      ${expandedModules.has(module._id) ? "bg-blue-100" : "bg-gray-50 hover:bg-gray-100"}
                    `}>
                    <td colSpan="8" className="px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <HiChevronRight
                            className={`
                              transform transition-transform duration-200 text-blue-500 text-lg
                              ${expandedModules.has(module._id) ? "rotate-90" : ""}
                            `}
                          />
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                            checked={moduleItems.every((item) => selectedItems.some((selectedItem) => selectedItem._id === item._id))}
                            onChange={() => handleSelectModule(moduleItems)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="font-medium text-gray-900">{module.title}</span>
                          <span className="text-sm text-gray-500">({moduleItems.length} items)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ModuleStats moduleItems={moduleItems} />
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const { ok, data } = await api.post("/roadlog_item", {
                                  project_id: project._id,
                                  module_id: module._id !== "-" ? module._id : null,
                                  module_name: module._id !== "-" ? module.title : null,
                                });

                                if (!ok) return toast.error("Failed to create item");

                                toast.success("Item created successfully");
                                setItems([...items, data]);

                                // Open the item modal
                                query.set("item_modal_id", data._id);
                                navigate({ search: query.toString() });
                              } catch (error) {
                                console.error(error);
                                toast.error("Failed to create item");
                              }
                            }}
                            className="rounded-full p-1 hover:bg-sky-50 text-black bg-white">
                            <HiPlus className="text-gray-500" />
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                  {expandedModules.has(module._id) &&
                    moduleItems.map((item, index) => {
                      const selected = selectedItems.some((i) => i._id === item._id);
                      return (
                        <tr
                          key={`${item._id}-${index}`}
                          className={`
                              relative group hover:bg-blue-50
                              ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                            `}>
                          <td
                            className="px-2 py-2"
                            onClick={() => {
                              if (selected) {
                                setSelectedItems(selectedItems.filter((i) => i._id !== item._id));
                              } else {
                                setSelectedItems([...selectedItems, item]);
                              }
                            }}>
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-gray-300 focus:border-blue-600 text-blue-600 focus:ring-0 focus:outline-none focus:shadow-none"
                              checked={selected}
                              onChange={() => {}}
                            />
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <div className="relative flex items-center gap-2 flex-1 min-w-0">
                                  <span className="font-medium shrink-0">#{item.index}</span>
                                  <div
                                    contentEditable
                                    suppressContentEditableWarning
                                    onBlur={(e) => {
                                      setEditingId(null);
                                      handleSave(item._id, { title: e.target.textContent });
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingId(item._id);
                                    }}
                                    className="outline-none border-b border-transparent hover:border-gray-300 focus:border-blue-500 bg-transparent px-1 line-clamp-3 overflow-hidden cursor-text">
                                    {item.title}
                                  </div>
                                  {item.nnl && (
                                    <span
                                      className={`text-xs px-1.5 rounded-full shrink-0 ${
                                        item.nnl === "NOW" ? "bg-blue-50 text-blue-700" : item.nnl === "NEXT" ? "bg-purple-50 text-purple-700" : "bg-gray-50 text-gray-700"
                                      }`}>
                                      {item.nnl}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => {
                                      query.set("item_modal_id", item._id);
                                      navigate({ search: query.toString() });
                                    }}
                                    className={`absolute top-1/2 -translate-y-1/2 right-0 px-3 py-1.5 rounded-md flex items-center gap-2 bg-white border border-gray-200 shadow-sm
                                        ${editingId === item._id ? "hidden" : "invisible group-hover:visible"}`}>
                                    <HiPencil className="w-4 h-4 text-gray-600" />
                                    <span className="text-xs font-medium text-gray-600">Open</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            {item.user_avatar && (
                              <div className="relative w-6 h-6 shrink-0">
                                <img src={item.user_avatar} alt={item.user_name} className="peer h-6 w-6 rounded-full hover:cursor-pointer" />
                                <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden peer-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                  {item.user_name}
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2 w-48">
                            <select
                              className={`w-full px-2 py-1 rounded cursor-pointer appearance-none text-xs`}
                              value={item.budget_id || ""}
                              onChange={(e) => {
                                if (!e.target.value) {
                                  handleSave(item._id, { budget_id: "", budget_name: "" });
                                  return;
                                }

                                const selectedBudget = budgets.find((b) => b._id === e.target.value);
                                handleSave(item._id, {
                                  budget_id: selectedBudget._id,
                                  budget_name: selectedBudget.name,
                                });
                              }}>
                              <option value="">Select a budget</option>
                              {budgets.map((budget) => (
                                <option key={budget._id} value={budget._id}>
                                  {budget.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-2 w-32">
                            <div
                              contentEditable
                              suppressContentEditableWarning
                              onBlur={(e) => {
                                const days = Number(e.target.textContent) || 0;
                                handleSave(item._id, {
                                  days_required: days,
                                  estimation: days * (item.tjm || 0),
                                });
                              }}
                              className="outline-none border-b border-transparent hover:border-gray-300 focus:border-blue-500 bg-transparent px-1 line-clamp-3 overflow-hidden cursor-text">
                              {item.days_required}
                            </div>
                          </td>
                          <td className="px-4 py-2">{formatNumberThreeDigits(item.estimation || 0)} €</td>
                          <td className="px-4 py-2 w-36">
                            <StatusTag
                              item={item}
                              onStatusChange={(newStatus) => {
                                handleSave(item._id, { status: newStatus });
                              }}
                            />
                          </td>
                          <td className="w-8 px-2">
                            <Menu as="div" className="relative">
                              <Menu.Button className="p-2 hover:bg-gray-100 rounded-lg">
                                <HiDotsVertical className="h-5 w-5 text-gray-500" />
                              </Menu.Button>
                              <Transition
                                as={Fragment}
                                enter="transition duration-200 ease-out"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="transition duration-150 ease-in"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0">
                                <Menu.Items className="absolute right-0 mt-1 w-48 origin-top-right bg-white rounded-xl shadow-lg border border-gray-200 p-1 focus:outline-none z-50">
                                  {index !== 0 && (
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          onClick={() => moveItem("up", module._id, index)}
                                          className={`${active ? "bg-gray-100" : ""} flex items-center gap-2 w-full px-4 py-3 text-gray-700 rounded-lg`}>
                                          <IoArrowUpOutline className="h-4 w-4 flex-shrink-0" />
                                          Move Up
                                        </button>
                                      )}
                                    </Menu.Item>
                                  )}
                                  {index !== moduleItems.length - 1 && (
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          onClick={() => moveItem("down", module._id, index)}
                                          className={`${active ? "bg-gray-100" : ""} flex items-center gap-2 w-full px-4 py-3 text-gray-700 rounded-lg`}>
                                          <IoArrowDownOutline className="h-4 w-4 flex-shrink-0" />
                                          Move Down
                                        </button>
                                      )}
                                    </Menu.Item>
                                  )}
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        onClick={() => handleDelete(item._id)}
                                        className={`${active ? "bg-red-50" : ""} flex items-center gap-2 w-full px-4 py-3 text-red-600 rounded-lg`}>
                                        <IoTrashOutline className="h-4 w-4 flex-shrink-0" />
                                        Delete Item
                                      </button>
                                    )}
                                  </Menu.Item>
                                </Menu.Items>
                              </Transition>
                            </Menu>
                          </td>
                        </tr>
                      );
                    })}
                </Fragment>
              );
            })
            .filter(Boolean)}
          <tr className="bg-sky-700 text-white text-sm border-t-2 border-gray-300">
            <td className="px-4 py-3"></td>
            <td className="px-4 py-3">Total</td>
            <td className="px-4 py-3"></td>
            <td className="px-4 py-3"></td>
            <td className="px-4 py-3">{items.reduce((acc, e) => acc + (e.days_required || 0), 0).toFixed(2)} days</td>
            <td className="px-4 py-3">{formatNumberThreeDigits(items.reduce((acc, e) => acc + Number(e.estimation || 0), 0))} €</td>
            <td className="px-4 py-3" colSpan="2">
              <div className="flex items-center gap-2 text-sm">
                <span>
                  {items.filter((e) => e.status === "DONE").length}/{items.length} completed
                </span>
                <span className="text-white">({items.length ? Math.round((items.filter((e) => e.status === "DONE").length / items.length) * 100) : 0}%)</span>
              </div>
            </td>
          </tr>
        </Table>
      </div>

      {itemModalId && (
        <ItemModal
          project={project}
          onClose={() => {
            query.delete("item_modal_id");
            navigate({ search: query.toString() });
          }}
          onUpdate={(item) => setItems(items.map((i) => (i._id === item._id ? item : i)))}
          onDelete={(item) => setItems(items.filter((i) => i._id !== item._id))}
        />
      )}
    </div>
  );
};

const StatusTag = ({ item, onStatusChange }) => {
  if (!item?.status) return null;
  const colors = {
    READY_TO_DEV: "bg-blue-400 text-white text-xs text-blue-600",
    TODO: "bg-blue-600 text-xs text-white border-gray-700",
    IN_PROGRESS: "bg-blue-100 text-xs text-blue-800",
    NEED_INFO: "bg-yellow-100 text-xs text-yellow-800",
    DONE: "bg-green-100 text-xs text-green-800",
    ARCHIVED: "bg-gray-100 text-xs text-gray-800",
    TESTING: "bg-purple-100 text-xs text-purple-800",
  };

  const handleStatusChange = (e) => {
    e.stopPropagation();
    onStatusChange(e.target.value);
  };

  return (
    <select className={`w-full px-2 py-1 rounded ${colors[item?.status]} cursor-pointer appearance-none hover:opacity-80`} value={item?.status} onChange={handleStatusChange}>
      <option value="READY_TO_DEV">READY TO DEV</option>
      <option value="TODO">TODO</option>
      <option value="IN_PROGRESS">IN PROGRESS</option>
      <option value="NEED_INFO">NEED INFO</option>
      <option value="DONE">DONE</option>
      <option value="ARCHIVED">ARCHIVED</option>
      <option value="TESTING">TESTING</option>
    </select>
  );
};

const ModuleStats = ({ moduleItems }) => {
  const stats = {
    total: moduleItems.length,
    done: moduleItems.filter((e) => e.status === "DONE").length,
    doneItems: moduleItems.filter((e) => e.status === "DONE"),
    inProgress: moduleItems.filter((e) => e.status === "IN_PROGRESS").length,
    totalDays: moduleItems.reduce((acc, e) => acc + Number(e.days_required || 0), 0),
    totalEstimation: moduleItems.reduce((acc, e) => acc + Number(e.estimation || 0), 0),
  };

  const spentEstimation = stats.doneItems.reduce((acc, e) => acc + Number(e.estimation || 0), 0);
  const percentComplete = stats.total ? Math.round((stats.done / stats.total) * 100) : 0;

  return (
    <div className="flex items-center gap-4 ml-4 text-xs text-gray-600">
      <span className="flex items-center gap-1">
        <IoStatsChart className="text-blue-500" />
        {stats.done}/{stats.total} completed
      </span>
      <span className="text-blue-600">{percentComplete}%</span>
      <span>{stats.totalDays?.toFixed(2)} days</span>
      <span>
        {formatNumberThreeDigits(spentEstimation)} €{" / "}
        {formatNumberThreeDigits(stats.totalEstimation)} €
      </span>
    </div>
  );
};

const formatNumberThreeDigits = (number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};
